// src/DriverDashboard.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { GoogleMap, LoadScript, Marker, Polyline, DirectionsRenderer } from "@react-google-maps/api";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const BACKEND_URL = process.env.REACT_APP_BASE_URL;
const WS_URL = process.env.REACT_APP_WS_URL;

// threshold in meters
const DEVIATION_THRESHOLD_METERS = 100; // 100m default
const STEP_ARRIVE_THRESHOLD_METERS = 20; // when to mark a step completed
const GOOGLE_MAP_LIBRARIES = ['places', 'geometry'];

export default function DriverDashboard() {
  const [routes, setRoutes] = useState([]);
  const [activeRoute, setActiveRoute] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDriving, setIsDriving] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [directionsResult, setDirectionsResult] = useState(null);
  const [steps, setSteps] = useState([]); // array of step objects from directions
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [distanceToDest, setDistanceToDest] = useState(null);
  const [etaToDest, setEtaToDest] = useState(null);

  const mapRef = useRef(null);
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);
  const deviationNotifiedRef = useRef(false);
  const lastDeviationSentAtRef = useRef(0);
  const directionsServiceRef = useRef(null);
  const distanceMatrixServiceRef = useRef(null);

  // --------------------------- helpers ---------------------------
  const decodePolyline = (encoded) => {
    if (!encoded) return [];
    // use google.maps.geometry.encoding if available; fallback to JS decoder
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.geometry &&
      window.google.maps.geometry.encoding
    ) {
      return window.google.maps.geometry.encoding
        .decodePath(encoded)
        .map((p) => ({ lat: p.lat(), lng: p.lng() }));
    }

    // fallback polyline decoder
    let points = [];
    let index = 0,
      len = encoded.length;
    let lat = 0,
      lng = 0;

    while (index < len) {
      let b,
        shift = 0,
        result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += dlat;

      shift = 0;
      result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += dlng;

      points.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return points;
  };

  const getBearing = (from, to) => {
    if (!window.google || !window.google.maps || !window.google.maps.geometry)
      return 0;
    return window.google.maps.geometry.spherical.computeHeading(
      new window.google.maps.LatLng(from.lat, from.lng),
      new window.google.maps.LatLng(to.lat, to.lng)
    );
  };

  // compute distance from point to each segment in polyline and return minimal distance (meters)
  const pointToPolylineDistance = (point, path) => {
    if (!window.google || !window.google.maps || !window.google.maps.geometry)
      return Infinity;
    const latLng = new window.google.maps.LatLng(point.lat, point.lng);
    let minDist = Infinity;
    for (let i = 0; i < path.length - 1; i++) {
      const a = new window.google.maps.LatLng(path[i].lat, path[i].lng);
      const b = new window.google.maps.LatLng(path[i + 1].lat, path[i + 1].lng);
      // Use distance to segment endpoints as a heuristic (fast and robust enough here)
      const distToA = window.google.maps.geometry.spherical.computeDistanceBetween(
        latLng,
        a
      );
      const distToB = window.google.maps.geometry.spherical.computeDistanceBetween(
        latLng,
        b
      );
      const candidate = Math.min(distToA, distToB);
      if (candidate < minDist) minDist = candidate;
    }
    return minDist;
  };

  const throttle = (fn, wait) => {
    let last = 0;
    return (...args) => {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn(...args);
      }
    };
  };

  // Helper function for consistent auto-focus behavior
  const autoFocusMapToRoute = (directionsResult) => {
    if (mapRef.current && directionsResult && directionsResult.routes[0]) {
      const bounds = new window.google.maps.LatLngBounds();
      directionsResult.routes[0].legs.forEach((leg) => {
        bounds.extend(leg.start_location);
        bounds.extend(leg.end_location);
        // Also extend bounds for each step to ensure complete route visibility
        leg.steps.forEach((step) => {
          bounds.extend(step.start_location);
          bounds.extend(step.end_location);
        });
      });
      try {
        mapRef.current.fitBounds(bounds);
      } catch (e) {
        // ignore fitBounds errors
        console.warn("fitBounds failed:", e);
      }
    }
  };

  // --------------------------- fetch routes ---------------------------
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${BACKEND_URL}/api/routes/driver/routes/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = res.data;

        if (!data.routes || data.routes.length === 0) {
          setRoutes([]);
          return;
        }

        const parsedRoutes = data.routes.map((entry) => {
          const routeData = entry.route;
          const points = entry.points || [];
          return {
            id: routeData.id || routeData.route_id,
            departure_date: routeData.departure_date,
            departure_time: routeData.departure_time,
            polyline: routeData.polyline,
            steps: entry.steps || [],
            // map backend points (latitude/longitude) to stops array used by UI
            stops: points.map((p) => ({
              lat: Number(p.latitude),
              lng: Number(p.longitude),
              address: p.address,
              order: p.order,
            })),
          };
        });

        setRoutes(parsedRoutes);
      } catch (err) {
        console.error("Error fetching routes", err);
        setError("Failed to load routes.");
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, []);

  // --------------------------- websocket ---------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/api/ws/driver?token=${token}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("✅ WS message received:", data); // ✅ Add this

        if (data.type === "ROUTE_DEVIATION" && data.self_alert) {
          const alertMsg = `⚠️ You deviated from route ${data.route_id || "N/A"} at (${data.lat?.toFixed(4)}, ${data.lng?.toFixed(4)})`;
          console.log("Triggering alert:", alertMsg); // ✅ Add this
          setNotification(alertMsg);
          setTimeout(() => setNotification(null), 8000);
        } else {
          console.log("ℹ️ Other WS message:", data); // ✅ Add this
        }
      } catch (e) {
        console.error("❌ WebSocket parse error:", e);
      }
    };


    ws.onopen = () => console.log("✅ Driver WebSocket connected");
    ws.onclose = () => console.log("🔌 Driver WebSocket disconnected");

    return () => {
      try {
        ws.close();
      } catch { }
    };
  }, []);

  // --------------------------- map load ---------------------------
  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    // instantiate services when google is available
    if (window.google && window.google.maps) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
      distanceMatrixServiceRef.current =
        new window.google.maps.DistanceMatrixService();
    }
  }, []);

  // --------------------------- route selection ---------------------------
  const handleRouteSelect = async (route) => {
    if (isDriving) return alert("Cannot switch routes while driving.");
    setActiveRoute(route);
    setDirectionsResult(null);
    setSteps([]);
    setCurrentStepIndex(0);
    deviationNotifiedRef.current = false;

    // Initial bounds fitting for polyline or stops (use polyline when available)
    if (mapRef.current) {
      try {
        const path = route.polyline ? decodePolyline(route.polyline) : [];
        const bounds = new window.google.maps.LatLngBounds();
        if (path && path.length > 0) {
          path.forEach((p) => bounds.extend(new window.google.maps.LatLng(p.lat, p.lng)));
        }
        if (route.stops && route.stops.length > 0) {
          route.stops.forEach((s) =>
            bounds.extend(new window.google.maps.LatLng(Number(s.lat), Number(s.lng)))
          );
        }
        // only fit if we actually added any points
        if ((path && path.length > 0) || (route.stops && route.stops.length > 0)) {
          mapRef.current.fitBounds(bounds);
        }
      } catch (e) {
        console.warn("Error fitting bounds on route select:", e);
      }
    }

    // Request DirectionsService using saves stops as waypoints (so we get steps)
    try {
      if (!directionsServiceRef.current) return;

      // origin/destination: prefer stops if present, otherwise decode polyline endpoints
      const decoded = route.polyline ? decodePolyline(route.polyline) : [];
      const origin = route.stops && route.stops.length > 0
        ? { lat: Number(route.stops[0].lat), lng: Number(route.stops[0].lng) }
        : decoded.length > 0
          ? decoded[0]
          : null;

      const destination = route.stops && route.stops.length > 0
        ? { lat: Number(route.stops[route.stops.length - 1].lat), lng: Number(route.stops[route.stops.length - 1].lng) }
        : decoded.length > 0
          ? decoded[decoded.length - 1]
          : null;

      if (!origin || !destination) return;

      const waypoints =
        route.stops && route.stops.length > 2
          ? route.stops.slice(1, -1).map((s) => ({
            location: { lat: Number(s.lat), lng: Number(s.lng) },
            stopover: true,
          }))
          : [];

      directionsServiceRef.current.route(
        {
          origin, destination, waypoints, travelMode: window.google.maps.TravelMode.DRIVING, drivingOptions: { departureTime: new Date() },
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirectionsResult(result);

            // ✨ AUTO-FOCUS LOGIC: Auto-focus map to fit the directions route bounds
            try {
              if (mapRef.current && result.routes[0]) {
                const bounds = new window.google.maps.LatLngBounds();
                result.routes[0].legs.forEach((leg) => {
                  bounds.extend(leg.start_location);
                  bounds.extend(leg.end_location);
                  // Also extend bounds for each step to ensure complete route visibility
                  leg.steps.forEach((step) => {
                    bounds.extend(step.start_location);
                    bounds.extend(step.end_location);
                  });
                });
                mapRef.current.fitBounds(bounds);
              }
            } catch (e) {
              console.warn("fitBounds on directions failed:", e);
            }

            // flatten steps from all legs
            const gatheredSteps = [];
            result.routes[0].legs.forEach((leg) => {
              leg.steps.forEach((s) => {
                gatheredSteps.push(s);
              });
            });
            setSteps(gatheredSteps);

            // small delay to ensure renderer updates cleanly
            setTimeout(() => setDirectionsResult(result), 200);
          } else {
            console.error("Directions request failed", status);
          }
        }
      );
    } catch (e) {
      console.error("Directions error", e);
    }
  };

  // helper to smoothly move map to target location
  const panToLocation = (latlng, zoom = 18) => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const currentCenter = map.getCenter();

    if (!currentCenter) return;

    // interpolate between current center and target
    const steps = 10;
    let i = 0;
    const deltaLat = (latlng.lat - currentCenter.lat()) / steps;
    const deltaLng = (latlng.lng - currentCenter.lng()) / steps;

    const animate = () => {
      if (i < steps) {
        const nextLat = currentCenter.lat() + deltaLat * i;
        const nextLng = currentCenter.lng() + deltaLng * i;
        map.panTo({ lat: nextLat, lng: nextLng });
        i++;
        requestAnimationFrame(animate);
      } else {
        // final position and zoom
        map.panTo(latlng);
        map.setZoom(zoom);
      }
    };

    animate();
  };

  // --------------------------- start/stop driving ---------------------------
  const startDriving = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported.");
    if (!activeRoute) return alert("Select a route first.");

    setIsDriving(true);
    deviationNotifiedRef.current = false;
    // start watch
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const latlng = { lat: latitude, lng: longitude };
        setCurrentPosition(latlng);
        onPositionUpdate(latlng);

        // smoothly pan map to driver location
        panToLocation(latlng, 15);
      },
      (err) => {
        console.error("GPS error:", err);
        alert("Unable to retrieve location.");
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
    );
  };

  const stopDriving = () => {
    setIsDriving(false);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    setCurrentPosition(null);
    setDistanceToDest(null);
    setEtaToDest(null);
    deviationNotifiedRef.current = false;

    // fit map to entire route
    if (mapRef.current && activeRoute) {
      const bounds = new window.google.maps.LatLngBounds();

      if (activeRoute.polyline) {
        decodePolyline(activeRoute.polyline).forEach((p) => {
          bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
        });
      }

      if (activeRoute.stops && activeRoute.stops.length > 0) {
        activeRoute.stops.forEach((s) =>
          bounds.extend(new window.google.maps.LatLng(Number(s.lat), Number(s.lng)))
        );
      }
      mapRef.current.fitBounds(bounds);
    }
  };

  // --------------------------- notify admin ---------------------------
  const sendDeviationToAdmin = async (pos) => {
    const now = Date.now();
    // avoid spamming: only send once every 60s per deviation
    if (now - lastDeviationSentAtRef.current < 60000) return;
    lastDeviationSentAtRef.current = now;

    try {
      const token = localStorage.getItem("token");
      // Try websocket first
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "driver_deviation", payload: { lat: pos.lat, lng: pos.lng, route_id: activeRoute.id } }));
      } else {
        // fallback to REST (if you have a backend endpoint for deviation)
        await axios.post(
          `${BACKEND_URL}/api/routes/deviation`,
          { route_id: activeRoute.id, lat: pos.lat, lng: pos.lng },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
      }
      setNotification("⚠️ You are Diverted from the Assigned Route. Deviation has been reported to admin");
      deviationNotifiedRef.current = true;
    } catch (e) {
      console.error("Failed to notify admin", e);
    }
  };

  // --------------------------- compute distance & ETA ---------------------------
  const updateDistanceAndETA = (pos) => {
    if (!distanceMatrixServiceRef.current || !directionsResult) return;
    try {
      const destinationLatLng = directionsResult.routes[0].legs.slice(-1)[0].end_location;
      distanceMatrixServiceRef.current.getDistanceMatrix(
        {
          origins: [new window.google.maps.LatLng(pos.lat, pos.lng)],
          destinations: [destinationLatLng],
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          if (status === window.google.maps.DistanceMatrixStatus.OK) {
            const elem = response.rows[0].elements[0];
            if (elem.status === "OK") {
              setDistanceToDest(elem.distance.text);
              setEtaToDest(elem.duration.text);
            }
          }
        }
      );
    } catch (e) {
      console.error("DistanceMatrix error", e);
    }
  };

  const throttledUpdateDistanceAndETA = useCallback(throttle(updateDistanceAndETA, 5000), [directionsResult]);

  // --------------------------- position updates ---------------------------
  const onPositionUpdate = (pos) => {
    // update distance/ETA periodically
    throttledUpdateDistanceAndETA(pos);

    // if we have route polyline, check deviation
    const path = activeRoute?.polyline
      ? decodePolyline(activeRoute.polyline)
      : directionsResult
        ? directionsResult.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }))
        : null;

    if (path && path.length > 1) {
      const distToPath = pointToPolylineDistance(pos, path); // meters

      if (distToPath > DEVIATION_THRESHOLD_METERS) {
        if (!deviationNotifiedRef.current) sendDeviationToAdmin(pos);
      } else {
        // driver back on route - clear deviation flag
        deviationNotifiedRef.current = false;
      }
    }

    // automatic step switching
    if (steps && steps.length > 0) {
      // find nearest upcoming step index
      for (let i = currentStepIndex; i < steps.length; i++) {
        const step = steps[i];
        const endLoc = { lat: step.end_location.lat(), lng: step.end_location.lng() };
        const dist = window.google.maps.geometry.spherical.computeDistanceBetween(
          new window.google.maps.LatLng(pos.lat, pos.lng),
          new window.google.maps.LatLng(endLoc.lat, endLoc.lng)
        );
        if (dist < STEP_ARRIVE_THRESHOLD_METERS) {
          setCurrentStepIndex(i + 1);
          // speak instruction for next step
          speakInstruction(steps[i + 1]);
        } else {
          // highlight current and stop scanning
          break;
        }
      }
    }
  };

  // --------------------------- voice guidance ---------------------------
  const speakInstruction = (step) => {
    if (!step) return;
    const text = step.instructions ? step.instructions.replace(/<[^>]*>?/gm, '') : '';
    if (!text) return;
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'en-US';
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    }
  };

  // --------------------------- render helpers ---------------------------
  const renderArrowMarker = () => {
    if (!currentPosition) return null;
    // find next point to compute bearing
    const path = directionsResult
      ? directionsResult.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }))
      : activeRoute?.polyline
        ? decodePolyline(activeRoute.polyline)
        : [];
    if (!path || path.length === 0) return null;

    // choose a point a little ahead on path based on currentStepIndex
    const nextPoint = path[Math.min(path.length - 1, currentStepIndex + 2)];
    const heading = nextPoint ? getBearing(currentPosition, nextPoint) : 0;

    return (
      <Marker
        position={currentPosition}
        icon={{
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          rotation: heading,
          scale: 5,
          fillColor: '#ff9800',
          fillOpacity: 1,
          strokeWeight: 1,
        }}
      />
    );
  };

  // --------------------------- effect: when directionsResult changes, set up steps ---------------------------
  useEffect(() => {
    if (!directionsResult) return;
    const gatheredSteps = [];
    directionsResult.routes[0].legs.forEach((leg) => {
      leg.steps.forEach((s) => gatheredSteps.push(s));
    });
    setSteps(gatheredSteps);
    setCurrentStepIndex(0);

    // Optional: Auto-focus when directions change (additional safety)
    autoFocusMapToRoute(directionsResult);
  }, [directionsResult]);

  // --------------------------- UI ---------------------------
  return (
    <div className="min-h-screen bg-[#1f1f1f] text-white flex flex-col lg:flex-row">
      {/* Sidebar */}
      <div className="lg:w-[30%] w-full p-6 bg-[#212121] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-orange-500">Driver Dashboard</h1>
          <button
            onClick={() => {
              localStorage.removeItem("token");
              window.location.href = "/";
            }}
            className="text-orange-400 hover:text-orange-500"
          >
            Logout
          </button>
        </div>

        {notification && (
          <div className="fixed top-10 right-10 z-50 bg-red-800 border border-red-500 text-white px-6 py-4 rounded-lg shadow-lg animate-fade-in-out transition-opacity duration-500">
            <div className="flex justify-between items-center">
              <div className="mr-4">{notification}</div>
              <button onClick={() => setNotification(null)} className="text-white hover:text-gray-200 text-lg">&times;</button>
            </div>
          </div>
        )}


        {loading && <p className="text-gray-400">Loading routes...</p>}
        {error && <p className="text-red-400">{error}</p>}

        {!loading && !error && (
          <>
            <h2 className="text-lg font-semibold text-orange-400 mb-4">Assigned Routes</h2>
            <div className="flex flex-col gap-4">
              {routes.length === 0 && <p className="text-gray-400">No routes available.</p>}
              {routes.map((route) => (
                <div
                  key={route.id}
                  className={`p-4 rounded-xl cursor-pointer transition ${activeRoute?.id === route.id ? "bg-orange-900 border border-orange-400" : "bg-[#2E2E2E] hover:bg-[#333]"
                    }`}
                  onClick={() => handleRouteSelect(route)}
                >
                  <p>
                    <span className="text-orange-400">Departure:</span> {route.departure_date} <span className="text-orange-400">at</span> {route.departure_time}
                  </p>
                  <p className="text-sm text-gray-300">Stops: {route.stops?.length || 0}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Distance & ETA */}
        {isDriving && (
          <div className="mt-6 bg-[#161616] p-3 rounded">
            <p className="text-sm text-gray-300">Distance to destination: <span className="font-semibold text-white">{distanceToDest || '—'}</span></p>
            <p className="text-sm text-gray-300">ETA: <span className="font-semibold text-white">{etaToDest || '—'}</span></p>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="flex-1 bg-black relative">
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAP_LIBRARIES}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100vh" }}
            center={{ lat: 20.5937, lng: 78.9629 }}
            zoom={5}
            mapTypeId="roadmap"
            onLoad={handleMapLoad}
          >
            {/* polyline from saved route (visual) */}
            {activeRoute?.polyline && (
              <Polyline
                path={decodePolyline(activeRoute.polyline)}
                options={{ strokeColor: "#1355cfff", strokeOpacity: 0.9, strokeWeight: 5 }}
              />
            )}

            {/* DirectionsRenderer (visual steps & overview) */}
            {directionsResult && <DirectionsRenderer directions={directionsResult} options={{ suppressMarkers: true }} />}

            {/* Stop markers */}
            {activeRoute?.stops?.map((stop, idx) => {
              let position = { lat: Number(stop.lat), lng: Number(stop.lng) };

              // Slightly offset last stop if it is the same as first stop
              if (
                idx === activeRoute.stops.length - 1 && // last stop
                activeRoute.stops.length > 1 &&
                Number(activeRoute.stops[0].lat) === Number(stop.lat) &&
                Number(activeRoute.stops[0].lng) === Number(stop.lng)
              ) {
                position = { lat: position.lat + 0.00005, lng: position.lng + 0.00005 };
              }

              return (
                <Marker
                  key={idx}
                  position={position}
                  label={{ text: `${idx + 1}`, color: "white", fontSize: "14px", fontWeight: "bold" }}
                />
              );
            })}

            {/* Arrow marker (driver) */}
            {currentPosition && renderArrowMarker()}

            {/* simple blue dot fallback marker */}
            {currentPosition && (
              <Marker
                position={currentPosition}
                icon={{ url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png", scaledSize: new window.google.maps.Size(40, 40) }}
              />
            )}
          </GoogleMap>
        </LoadScript>

        {/* Driving buttons */}
        {activeRoute && !isDriving && (
          <button onClick={startDriving} className="absolute top-4 right-4 z-10 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded shadow-md">Start Driving</button>
        )}
        {isDriving && (
          <button onClick={stopDriving} className="absolute top-4 right-4 z-10 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded shadow-md">Stop Driving</button>
        )}

        {/* Navigation instructions (with active step highlighting) */}
        {steps.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-[#1f1f1f] text-sm text-white p-4 max-h-[260px] overflow-y-auto border-t border-gray-700">
            <h3 className="text-orange-400 font-semibold mb-2">Navigation Instructions</h3>
            <ul className="space-y-2">
              {steps.map((instruction, idx) => {
                const html = instruction.instructions;
                const isActive = idx === currentStepIndex;
                return (
                  <li key={idx} className={`p-2 rounded ${isActive ? 'bg-[#2a2a2a] border-l-4 border-orange-400' : 'bg-transparent'}`}>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-gray-300" dangerouslySetInnerHTML={{ __html: html }} />
                      <div className="text-xs text-gray-400">
                        {instruction.distance?.text}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
